import { useEffect, useState } from "react";
import { X, Sparkles, Loader2, HeartCrack, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { cancelSubscription, acceptSaveOffer, saveOfferShown } from "../lib/api";

// Same enum values Stripe accepts for cancellation_details.feedback so this
// data lines up with the /admin/cancellations report.
const REASONS = [
  { key: "too_expensive",   label: "Too expensive" },
  { key: "missing_features",label: "Missing a feature I need" },
  { key: "unused",          label: "Not using it enough" },
  { key: "switched_service",label: "Switched to something else" },
  { key: "too_complex",     label: "Too complex" },
  { key: "low_quality",     label: "Quality issues" },
  { key: "customer_service",label: "Customer service" },
  { key: "other",           label: "Other" },
];

// Two-step cancel flow:
// - Step "offer": show the 50% save offer (skipped if already used or user picks decline)
// - Step "reason": pick a reason + optional comment + confirm cancellation
// onDone(kind) is called with kind = "saved" | "canceled" so the parent can refresh.
export default function CancelModal({ open, onClose, saveOfferAvailable, onDone }) {
  const [step, setStep] = useState(saveOfferAvailable ? "offer" : "reason");
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(null); // "accept" | "cancel" | null

  // Log that the offer was displayed (once) so admin analytics can compute save-rate.
  useEffect(() => {
    if (open && step === "offer" && saveOfferAvailable) {
      saveOfferShown();
    }
  }, [open, step, saveOfferAvailable]);

  if (!open) return null;

  const acceptOffer = async () => {
    setBusy("accept");
    try {
      await acceptSaveOffer();
      toast.success("Nice — 50% off applied to your next invoice");
      onDone?.("saved");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't apply the offer");
    } finally {
      setBusy(null);
    }
  };

  const confirmCancel = async () => {
    setBusy("cancel");
    try {
      const res = await cancelSubscription({ reason, comment });
      const when = res?.expires_at ? new Date(res.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "the end of your period";
      toast.success(`Cancelled — you keep access until ${when}`);
      onDone?.("canceled");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="cancel-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={() => !busy && onClose()}
    >
      <div
        className="w-full max-w-lg border border-white/15 bg-neutral-950 shadow-[0_0_0_1px_rgba(255,255,255,0.05)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div className="flex items-center gap-2">
            {step === "offer" ? <Sparkles className="h-4 w-4 text-[#FF3B30]" /> : <HeartCrack className="h-4 w-4 text-neutral-400" />}
            <h2 className="font-display text-2xl uppercase tracking-tight text-white">
              {step === "offer" ? "Wait — one thing" : "Cancel Pro Pass"}
            </h2>
          </div>
          <button
            data-testid="cancel-modal-close"
            onClick={() => !busy && onClose()}
            className="border border-white/15 p-1 text-neutral-400 hover:bg-white hover:text-black"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {step === "offer" ? (
          <div className="px-5 py-6" data-testid="cancel-step-offer">
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">Save offer · one-time</p>
            <h3 className="mt-2 font-display text-4xl uppercase leading-tight text-white">
              Take 50% off<br />next month?
            </h3>
            <p className="mt-3 text-sm text-neutral-300">
              We know money adds up. Stick around one more month for <span className="text-white">$2.50 instead of $5</span> — same access, half price. Applied instantly to your next invoice.
            </p>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
              This offer is only available once per subscription.
            </p>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              <button
                data-testid="cancel-offer-decline"
                onClick={() => setStep("reason")}
                disabled={busy != null}
                className="border border-white/15 bg-transparent px-4 py-3 font-mono text-[11px] uppercase tracking-widest text-neutral-300 hover:border-neutral-400 hover:text-white disabled:opacity-50"
              >
                No thanks — cancel <ArrowRight className="ml-1 inline h-3 w-3" />
              </button>
              <button
                data-testid="cancel-offer-accept"
                onClick={acceptOffer}
                disabled={busy != null}
                className="flex items-center justify-center gap-2 border border-white/20 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black disabled:opacity-50"
              >
                {busy === "accept" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Yes — 50% off next month
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-6" data-testid="cancel-step-reason">
            <p className="text-sm text-neutral-300">
              Sorry to see you go. You&apos;ll keep Pro Pass access until the end of your current period — no interruption, no partial refund needed. Mind sharing why?
            </p>

            <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="cancel-reason-grid">
              {REASONS.map((r) => (
                <button
                  key={r.key}
                  data-testid={`cancel-reason-${r.key}`}
                  onClick={() => setReason(r.key)}
                  className={`border px-3 py-2 text-left font-mono text-[10px] uppercase tracking-widest transition-colors ${reason === r.key ? "border-[#FF3B30] bg-[#FF3B30]/10 text-[#FF3B30]" : "border-white/15 text-neutral-400 hover:border-white/40 hover:text-white"}`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <textarea
              data-testid="cancel-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Anything else? (optional)"
              rows={3}
              className="mt-4 w-full resize-none border border-white/15 bg-black p-3 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-white/40"
            />

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
              <button
                data-testid="cancel-step-back"
                onClick={() => saveOfferAvailable ? setStep("offer") : onClose()}
                disabled={busy != null}
                className="border border-white/15 bg-transparent px-4 py-3 font-mono text-[11px] uppercase tracking-widest text-neutral-300 hover:border-neutral-400 hover:text-white disabled:opacity-50"
              >
                {saveOfferAvailable ? "← Back" : "Never mind"}
              </button>
              <button
                data-testid="cancel-confirm-btn"
                onClick={confirmCancel}
                disabled={busy != null}
                className="flex items-center justify-center gap-2 border border-neutral-500/40 bg-neutral-800 px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black disabled:opacity-50"
              >
                {busy === "cancel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <HeartCrack className="h-4 w-4" />}
                Cancel Pro Pass
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
